import { c } from "react/compiler-runtime";
import { useCache } from "@effektify/cache";
import { useState, useEffect } from "react";
import useDataProvider from "./useDataProvider.js";
import useLogger from "./useLogger.js";
import { isDataSourceDefinition, isDataSource } from "../types/src/guards/data.js";
const defaultReturn = {
  data: null,
  isPending: false,
  isLoading: false,
  isError: false
};
const useCardData = CardConfig => {
  const $ = c(23);
  const logger = useLogger("useCardsData");
  const {
    getData,
    getDataSource,
    setDataSource,
    dataVersion
  } = useCache();
  let t0;
  if ($[0] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [cardData, setCardData] = useState(t0);
  const {
    data: dataSources
  } = CardConfig;
  const {
    isLoading: isFetchingData
  } = useDataProvider();
  let t1;
  if ($[1] !== getData || $[2] !== isFetchingData || $[3] !== logger) {
    t1 = options => {
      const {
        source
      } = options;
      const {
        data,
        isError: t22
      } = getData(source);
      const isError = t22 === void 0 ? false : t22;
      logger.debug(`Checking data source (${source})`, {
        data,
        isError
      });
      if (isFetchingData || isError || !data) {
        if (isError) {
          logger.error(`Data source (${source}) is error`, {
            data
          });
        }
        return {
          ...defaultReturn,
          isPending: isFetchingData,
          isError
        };
      }
      logger.debug(`Defining data (${source}), is fetched and ready`, {
        data
      });
      return {
        ...defaultReturn,
        data
      };
    };
    $[1] = getData;
    $[2] = isFetchingData;
    $[3] = logger;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const checkSourceData = t1;
  let t2;
  if ($[5] !== checkSourceData || $[6] !== dataSources || $[7] !== getDataSource || $[8] !== isFetchingData || $[9] !== logger || $[10] !== setDataSource) {
    t2 = options_0 => {
      if (isFetchingData) {
        logger.debug("Data source is fetching data");
        return {
          ...defaultReturn,
          isPending: true
        };
      }
      if (isDataSourceDefinition(options_0)) {
        const {
          key
        } = options_0;
        logger.debug(`defining dataSource: ${key}`);
        const currentData = getDataSource(key);
        if (currentData.data) {
          logger.debug(`Data source (${key}) is already defined`, {
            currentData
          });
          return {
            ...defaultReturn,
            ...currentData
          };
        }
        const sourceData = checkSourceData(options_0);
        const {
          data: data_0,
          isPending,
          isError: isError_0
        } = sourceData;
        if (isError_0) {
          logger.debug(`Data source (${key}) is pending or error`);
          return {
            ...defaultReturn,
            data: data_0,
            isPending,
            isError: isError_0
          };
        }
        if (!data_0) {
          logger.debug(`Data source (${key}) has no data`);
          return {
            ...defaultReturn,
            isPending: false,
            isError: true
          };
        }
        logger.debug(`Data source (${key}) is defined`, {
          data: data_0
        });
        const filteredSourceData = options_0.fromDataSource(data_0);
        setDataSource(key, filteredSourceData);
        const datasource = {
          ...defaultReturn,
          data: filteredSourceData
        };
        return datasource;
      }
      logger.debug(`Data source (${dataSources}) is not a valid data source`);
      return {
        ...defaultReturn
      };
    };
    $[5] = checkSourceData;
    $[6] = dataSources;
    $[7] = getDataSource;
    $[8] = isFetchingData;
    $[9] = logger;
    $[10] = setDataSource;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const defineCardData = t2;
  let t3;
  if ($[12] !== dataSources || $[13] !== defineCardData || $[14] !== isFetchingData || $[15] !== logger) {
    t3 = () => {
      logger.debug("useEffect[dataSources, defineCardData, isFetchingData]", {
        dataSources,
        isFetchingData
      });
      if (!dataSources || !isDataSource(dataSources) || isFetchingData) {
        return;
      }
      const resolved = Object.entries(dataSources.sources).reduce((acc, t42) => {
        const [key_0, value] = t42;
        if (isDataSourceDefinition(value)) {
          const dataSource = defineCardData(value);
          if (dataSource && dataSource.data) {
            acc[key_0] = dataSource;
          } else {
            logger.debug(`Data source (${key_0}) is not defined or has no data`, {
              value
            });
          }
        } else {
          logger.debug(`Data source (${key_0}) is not a valid data source definition`, {
            value
          });
        }
        return acc;
      }, {});
      setCardData(prev => {
        if (Object.keys(resolved).length === 0) {
          logger.debug("No data sources resolved, returning previous state");
          return prev;
        }
        if (JSON.stringify(prev) === JSON.stringify(resolved)) {
          logger.debug("No change in card data, returning previous state");
          return prev;
        }
        return resolved;
      });
    };
    $[12] = dataSources;
    $[13] = defineCardData;
    $[14] = isFetchingData;
    $[15] = logger;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== dataSources || $[18] !== dataVersion || $[19] !== defineCardData || $[20] !== isFetchingData || $[21] !== logger) {
    t4 = [logger, dataSources, defineCardData, isFetchingData, dataVersion];
    $[17] = dataSources;
    $[18] = dataVersion;
    $[19] = defineCardData;
    $[20] = isFetchingData;
    $[21] = logger;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  useEffect(t3, t4);
  return cardData;
};
export { useCardData as default };